import { useEffect, useMemo, useState } from 'react';
import { Check, ChevronDown, GripVertical, Image as ImageIcon, Layers, Plus, Trash2, Upload, X } from 'lucide-react';
import { DndContext, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { compressImage } from '../../utils/imageCompress';
import { useLang } from '../../LangContext';

const LANGUAGES = ['tr', 'en', 'de'];
const LANGUAGE_LABEL = { tr: 'Türkçe', en: 'English', de: 'Deutsch' };

// Kalıp tiplerini yönetme ekranı: sol tarafta kalıp listesi, sağda seçili kalıbın
// kapak görseli ve dile göre (tr zorunlu değil ama önerilir, en/de opsiyonel) form ilişkilendirmesi.
// Bu ekran, eski "kullanıcıya tek tek form atama" akışının yerini alır — kullanıcı artık
// önce bir kalıp seçer, ardından kendi diline uygun form otomatik olarak açılır.
export default function MoldTypesPage({ moldTypes, forms, createMoldType, updateMoldType, deleteMoldType, linkMoldForm, unlinkMoldForm, uploadMoldImage, reorderMoldTypes }) {
  const { locale: t } = useLang();
  const mt = t.moldTypes;
  const [selectedId, setSelectedId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [uploading, setUploading] = useState(false);
  // Sürükle-bırağın optimistic güncellemesi için yerel ayna — sunucu yanıtı gelene kadar anlık sıra gösterir
  const [localMolds, setLocalMolds] = useState(moldTypes || []);

  useEffect(() => {
    setLocalMolds(moldTypes || []);
  }, [moldTypes]);

  const sortedMolds = useMemo(
    () => [...localMolds].sort((a, b) => a.sortOrder - b.sortOrder || a.nameTr.localeCompare(b.nameTr, 'tr')),
    [localMolds],
  );

  // Kalıp sürükle-bırak: 5px eşik ile tıklama/sürükleme ayrımı yapılır
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  async function handleDragEnd(event) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = sortedMolds.findIndex((mold) => mold.id === active.id);
    const newIndex = sortedMolds.findIndex((mold) => mold.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(sortedMolds, oldIndex, newIndex).map((mold, index) => ({ ...mold, sortOrder: index }));
    setLocalMolds(reordered);
    await reorderMoldTypes(reordered.map((mold) => mold.id));
  }

  // Liste güncellenince seçili kalıbın taze halini eşle; seçili kalıp artık yoksa seçimi temizle
  const selectedMold = useMemo(
    () => sortedMolds.find((mold) => mold.id === selectedId) || null,
    [sortedMolds, selectedId],
  );

  useEffect(() => {
    if (!selectedId && sortedMolds.length > 0) {
      setSelectedId(sortedMolds[0].id);
    }
  }, [sortedMolds, selectedId]);

  const activeForms = useMemo(() => (forms || []).filter((form) => form.isActive !== false), [forms]);

  async function handleCreate() {
    setCreating(true);
    try {
      const created = await createMoldType({ nameTr: mt.newMoldDefaultName, nameEn: null, nameDe: null, imageUrl: null, sortOrder: sortedMolds.length });
      if (created?.id) {
        setSelectedId(created.id);
      }
    } finally {
      setCreating(false);
    }
  }

  async function handleDelete(mold) {
    if (!window.confirm(mt.confirmDelete(mold.nameTr))) return;
    const ok = await deleteMoldType(mold.id);
    if (ok && selectedId === mold.id) {
      setSelectedId(null);
    }
  }

  async function handleImageUpload(file) {
    if (!file || !selectedMold) return;
    setUploading(true);
    try {
      const blob = await compressImage(file, 640, 0.8);
      const url = await uploadMoldImage(blob);
      if (url) {
        await updateMoldType(selectedMold.id, {
          nameTr: selectedMold.nameTr,
          nameEn: selectedMold.nameEn,
          nameDe: selectedMold.nameDe,
          imageUrl: url,
          isActive: selectedMold.isActive,
          sortOrder: selectedMold.sortOrder,
        });
      }
    } catch (error) {
      window.alert(error?.message || mt.uploadError);
    } finally {
      setUploading(false);
    }
  }

  return (
    <section className="section page-panel mt-page">
      <div className="section-header">
        <div>
          <h2><Layers size={20} /> {mt.title}</h2>
          <p className="muted">{mt.subtitle}</p>
        </div>
        <button type="button" className="btn btn-primary" onClick={handleCreate} disabled={creating}>
          <Plus size={16} /> {mt.newMold}
        </button>
      </div>

      <div className="mt-layout">
        {/* Sol: kalıp listesi — sürükle-bırak ile sıralanır (kullanıcıya gösterim sırası) */}
        <div className="mt-list">
          {sortedMolds.length === 0 && (
            <div className="empty-state">{mt.noMolds}</div>
          )}
          {sortedMolds.length > 0 && (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={sortedMolds.map((mold) => mold.id)} strategy={verticalListSortingStrategy}>
                {sortedMolds.map((mold) => (
                  <SortableMoldRow
                    key={mold.id}
                    mold={mold}
                    isSelected={selectedId === mold.id}
                    onSelect={() => setSelectedId(mold.id)}
                    mt={mt}
                  />
                ))}
              </SortableContext>
            </DndContext>
          )}
        </div>

        {/* Sağ: seçili kalıbın detayı */}
        <div className="mt-detail">
          {!selectedMold ? (
            <div className="empty-state">{mt.selectMoldHint}</div>
          ) : (
            <MoldDetailEditor
              key={selectedMold.id}
              mold={selectedMold}
              forms={activeForms}
              mt={mt}
              uploading={uploading}
              onUpdate={updateMoldType}
              onDelete={handleDelete}
              onImageUpload={handleImageUpload}
              onLinkForm={linkMoldForm}
              onUnlinkForm={unlinkMoldForm}
            />
          )}
        </div>
      </div>
    </section>
  );
}

// Sürükle tutamağı yalnızca ayrı bir kolda — satırın geri kalanı normal tıklamayla seçilir
function SortableMoldRow({ mold, isSelected, onSelect, mt }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: mold.id });
  const style = { transform: CSS.Transform.toString(transform), transition };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`mt-list-item${isSelected ? ' is-active' : ''}${!mold.isActive ? ' is-inactive' : ''}${isDragging ? ' is-dragging' : ''}`}
    >
      <button type="button" className="drag-handle" aria-label="Kalıbı sürükle" {...attributes} {...listeners}>
        <GripVertical size={14} />
      </button>
      <button type="button" className="mt-list-select" onClick={onSelect}>
        <span className="mt-list-thumb">
          {mold.imageUrl ? <img src={mold.imageUrl} alt={mold.nameTr} /> : <ImageIcon size={20} />}
        </span>
        <span className="mt-list-body">
          <strong>{mold.nameTr}</strong>
          <span className="mt-list-langs">
            {LANGUAGES.map((lang) => (
              <span key={lang} className={`mt-lang-dot${mold.forms.some((f) => f.language === lang) ? ' is-set' : ''}`} title={LANGUAGE_LABEL[lang]}>
                {lang.toUpperCase()}
              </span>
            ))}
          </span>
        </span>
        {!mold.isActive && <span className="mt-list-badge">{mt.inactive}</span>}
      </button>
    </div>
  );
}

function MoldDetailEditor({ mold, forms, mt, uploading, onUpdate, onDelete, onImageUpload, onLinkForm, onUnlinkForm }) {
  async function toggleActive() {
    await onUpdate(mold.id, {
      nameTr: mold.nameTr, nameEn: mold.nameEn, nameDe: mold.nameDe,
      imageUrl: mold.imageUrl, isActive: !mold.isActive, sortOrder: mold.sortOrder,
    });
  }

  // Belirli bir dildeki kalıp adını günceller — diğer alanlara dokunmadan
  async function commitMoldName(language, value) {
    const key = { tr: 'nameTr', en: 'nameEn', de: 'nameDe' }[language];
    const current = mold[key] || '';
    const trimmed = value.trim();
    if (trimmed === current) return;
    if (language === 'tr' && !trimmed) return; // Türkçe ad boş bırakılamaz
    await onUpdate(mold.id, {
      nameTr: mold.nameTr, nameEn: mold.nameEn, nameDe: mold.nameDe,
      [key]: trimmed || null,
      imageUrl: mold.imageUrl, isActive: mold.isActive, sortOrder: mold.sortOrder,
    });
  }

  return (
    <div className="mt-detail-card">
      <div className="mt-detail-head">
        <div className="mt-cover">
          {mold.imageUrl ? <img src={mold.imageUrl} alt={mold.nameTr} /> : <span className="mt-cover-placeholder"><ImageIcon size={32} /></span>}
          <label className="mt-cover-upload" title={mt.uploadImage}>
            <input type="file" accept="image/*" onChange={(event) => onImageUpload(event.target.files?.[0])} disabled={uploading} />
            <Upload size={16} /> {uploading ? mt.uploading : mt.uploadImage}
          </label>
        </div>

        <div className="mt-detail-fields">
          <label className="mt-active-toggle">
            <input type="checkbox" checked={mold.isActive} onChange={toggleActive} />
            <span>{mold.isActive ? mt.active : mt.inactive}</span>
          </label>

          <button type="button" className="btn btn-ghost-danger" onClick={() => onDelete(mold)}>
            <Trash2 size={14} /> {mt.deleteMold}
          </button>
        </div>
      </div>

      <div className="mt-lang-section">
        <h3>{mt.moldNameTitle}</h3>
        <p className="muted">{mt.moldNameHint}</p>
        <div className="mt-lang-grid">
          {LANGUAGES.map((lang) => (
            <MoldNameSlot key={lang} language={lang} mold={mold} mt={mt} onCommit={commitMoldName} />
          ))}
        </div>
      </div>

      <div className="mt-lang-section">
        <h3>{mt.languageFormsTitle}</h3>
        <p className="muted">{mt.languageFormsHint}</p>
        <div className="mt-lang-grid">
          {LANGUAGES.map((lang) => (
            <LanguageFormSlot
              key={lang}
              language={lang}
              mold={mold}
              forms={forms}
              mt={mt}
              onLink={onLinkForm}
              onUnlink={onUnlinkForm}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function MoldNameSlot({ language, mold, mt, onCommit }) {
  const key = { tr: 'nameTr', en: 'nameEn', de: 'nameDe' }[language];
  const [value, setValue] = useState(mold[key] || '');
  const [saving, setSaving] = useState(false);

  async function handleBlur() {
    setSaving(true);
    try {
      await onCommit(language, value);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="mt-lang-slot">
      <div className="mt-lang-slot-head">
        <span className="mt-lang-slot-code">{language.toUpperCase()}</span>
        <span className="mt-lang-slot-name">{LANGUAGE_LABEL[language]}</span>
        {language === 'tr' && <span className="mt-lang-slot-hint">{mt.required}</span>}
      </div>
      <input
        className="mt-name-input"
        value={value}
        placeholder={language === 'tr' ? mt.moldNamePlaceholderRequired : mt.moldNamePlaceholderOptional}
        onChange={(event) => setValue(event.target.value)}
        onBlur={handleBlur}
        disabled={saving}
      />
    </div>
  );
}

function LanguageFormSlot({ language, mold, forms, mt, onLink, onUnlink }) {
  const link = mold.forms.find((f) => f.language === language) || null;
  const [pending, setPending] = useState(false);

  async function handleSelect(event) {
    const formTemplateId = event.target.value;
    if (!formTemplateId) return;
    setPending(true);
    try {
      await onLink(mold.id, language, formTemplateId);
    } finally {
      setPending(false);
    }
  }

  async function handleRemove() {
    setPending(true);
    try {
      await onUnlink(mold.id, language);
    } finally {
      setPending(false);
    }
  }

  return (
    <div className={`mt-lang-slot${link ? ' is-linked' : ''}`}>
      <div className="mt-lang-slot-head">
        <span className="mt-lang-slot-code">{language.toUpperCase()}</span>
        <span className="mt-lang-slot-name">{LANGUAGE_LABEL[language]}</span>
        {language === 'tr' && <span className="mt-lang-slot-hint">{mt.recommended}</span>}
      </div>

      {link ? (
        <div className="mt-lang-slot-linked">
          <span className="mt-lang-slot-check"><Check size={14} /></span>
          <span className="mt-lang-slot-formname" title={link.formName}>{link.formName}</span>
          <button type="button" className="mt-lang-slot-remove" onClick={handleRemove} disabled={pending} title={mt.removeLink}>
            <X size={14} />
          </button>
        </div>
      ) : (
        <div className="mt-select-wrap">
          <select onChange={handleSelect} defaultValue="" disabled={pending}>
            <option value="">{mt.pickForm}</option>
            {forms.map((form) => <option key={form.id} value={form.id}>{form.name}</option>)}
          </select>
          <ChevronDown size={14} className="mt-select-icon" />
        </div>
      )}
    </div>
  );
}
